import React, { useState, useRef, useEffect } from 'react';
import { phoneCountryCodes } from '../../lib/countries';
import { ChevronDown } from 'lucide-react';
import { InputProps, PhoneInputProps, SelectProps, ButtonProps } from './types';

export const Input: React.FC<InputProps> = ({ label, type = 'text', value, onChange, placeholder, required }) => (
  <div className="space-y-2">
    <label className="block text-sm font-bold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      required={required}
      placeholder={placeholder}
      className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-400 outline-none transition-all font-medium"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

export const PhoneInput: React.FC<PhoneInputProps> = ({ label, countryCode, phone, onCountryCodeChange, onPhoneChange, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getPlaceholder = (code: string) => {
    switch (code) {
      case '+1': return '(555) 000-0000';
      case '+44': return '7xxx xxxxxx';
      case '+61': return '04xx xxx xxx';
      case '+49': return '1xx xxxxxxxx';
      case '+33': return '06 xx xx xx xx';
      case '+91': return '00000-00000';
      case '+63': return '9xx xxxx xxx';
      default: return 'Enter phone number';
    }
  };

  const currentCountry = phoneCountryCodes.find(c => c.code === countryCode) || phoneCountryCodes[0];

  const filteredCountries = phoneCountryCodes.filter(item =>
    item.country.toLowerCase().includes(search.toLowerCase()) ||
    item.code.includes(search)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    onCountryCodeChange(code);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-bold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-purple-500 overflow-visible">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="px-3 py-3 bg-transparent border-none outline-none font-medium cursor-pointer flex items-center gap-1 hover:bg-gray-100 rounded-l-xl transition-colors"
          >
            <span>{currentCountry.flag}</span>
            <span>{currentCountry.code}</span>
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  placeholder="Search country..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-400"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((item) => (
                    <button
                      key={`${item.code}-${item.country}`}
                      type="button"
                      onClick={() => handleSelect(item.code)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2 transition-colors ${item.code === countryCode ? 'bg-purple-50 text-purple-700' : ''
                        }`}
                    >
                      <span className="text-base">{item.flag}</span>
                      <span className="flex-1 truncate">{item.country}</span>
                      <span className="text-gray-500 font-medium">{item.code}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-gray-400">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300"></div>
        <input
          type="tel"
          required={required}
          placeholder={getPlaceholder(countryCode)}
          className="flex-1 px-4 py-3 bg-transparent border-none outline-none font-medium min-w-0"
          value={phone}
          onChange={e => onPhoneChange(e.target.value)}
        />
      </div>
    </div>
  );
};

export const Select: React.FC<SelectProps> = ({ label, value, onChange, options, required }) => (
  <div className="space-y-2">
    <label className="block text-sm font-bold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <select
        required={required}
        className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-400 outline-none font-medium appearance-none"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {options.map((opt: string) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">▼</div>
    </div>
  </div>
);

export const Button: React.FC<ButtonProps> = ({ children, onClick, variant = 'primary' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-8 py-3 rounded-xl font-bold transition-all ${variant === 'primary'
      ? 'bg-black text-white hover:bg-purple-700 shadow-lg shadow-purple-500/10'
      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
  >
    {children}
  </button>
);
